import { EventEmitter, Injectable } from '@angular/core';
import { ApiService, GenericResponsePage } from '@uranda/commons';
import { NotificationModel, NotificationType } from '../model/notification.model';
import { HttpParams } from '@angular/common/http';
import { WebSocketService } from './websocket.service';
import { NotificationUtils } from './utils/notification-utils';
import { AlertNotificationsService } from './alert-notifications.service';

@Injectable({
  providedIn: 'root',
})
export class NotifificationService extends ApiService {
  notifications: NotificationModel[] = [];
  private initialized = false;

  wsService: WebSocketService = this.injector.get(WebSocketService);
  alertNotificationsService: AlertNotificationsService = this.injector.get(AlertNotificationsService);
  onNewRequest = new EventEmitter();
  onNewContactRequest = new EventEmitter();
  onNewQuoteRequest = new EventEmitter();
  onNewQuoteInterest = new EventEmitter();
  onPartnerUpdated = new EventEmitter();
  onPartnerCreated = new EventEmitter();
  onNewSupportRequest = new EventEmitter();
  onWalletUpdate = new EventEmitter();
  onAvailableQuotesChanged = new EventEmitter<void>();
  onSupportRequestResolved = new EventEmitter<void>();

  init() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.wsService.notifications$.subscribe((msg) => {
      if (!msg) return;
      // Evento tecnico: aggiorna solo il contatore "richieste disponibili in zona".
      // Non è una notifica persistita → niente refetch della campanella, niente toast
      // generico (che farebbe anche suonare), niente onNewRequest.
      if (msg.message === NotificationType.AVAILABLE_QUOTES_CHANGED) {
        this.onAvailableQuotesChanged.emit();
        return;
      }
      // Evento tecnico: una riga esce dalla tabella Support. Niente toast/campanella.
      if (msg.message === NotificationType.SUPPORT_REQUEST_RESOLVED) {
        this.onSupportRequestResolved.emit();
        return;
      }
      this.getNotifications(0, 100, false);

      this.manageNotificationType(msg?.message);
    });
  }

  async getNotifications(
    page: number = 0,
    size: number = 100,
    read?: boolean
  ): Promise<GenericResponsePage<NotificationModel>> {
    let queryParameters = new HttpParams()
      .append('page', page.toString())
      .append('size', size.toString());

    if (read !== undefined) {
      queryParameters = queryParameters.append('read', read.toString());
    }
    const response = (await this.get('/v1/notifications', queryParameters)).data;
    this.mapNotifiations(response.content);
    return response.data;
  }

  private mapNotifiations(notifications: NotificationModel[]) {
    this.notifications = notifications.map(
      (notification: NotificationModel) => ({
        ...notification,
        ...NotificationUtils.getNotificationIcon(notification.type),
        isRead: !!notification.readAt,
      })
    );
  }

  async markAsRead(notificationId: number): Promise<boolean> {
    const index = this.notifications.findIndex((n) => n.id === notificationId);
    const removed = index >= 0 ? this.notifications.splice(index, 1)[0] : undefined;
    try {
      const response = await this.post(`/v1/notifications/${notificationId}/mark-as-read`, {});
      return response.data;
    } catch (error) {
      if (removed) {
        this.notifications.splice(index, 0, removed);
      }
      throw error;
    }
  }

  async markAllAsRead(): Promise<boolean> {
    const previous = this.notifications;
    this.notifications = [];
    try {
      const response = await this.post('/v1/notifications/mark-all-as-read', {});
      return response.data;
    } catch (error) {
      this.notifications = previous;
      throw error;
    }
  }

  private manageNotificationType(type: NotificationType) {
    // REQUEST_EXPIRED è un evento di sistema: alla scadenza la card sparisce
    // e basta. Mostrare il toast generico qui significava impilarne uno per
    // ogni richiesta scaduta nello stesso istante (7 card → 7 toast identici).
    if (type !== NotificationType.REQUEST_EXPIRED) {
      this.alertNotificationsService.info('You have a new notification');
    }
    switch (type) {
      case NotificationType.NEW_CONTACT_REQUEST:
        this.onNewContactRequest.emit();
        break;
      case NotificationType.NEW_QUOTE_REQUEST:
        this.onNewQuoteRequest.emit();
        break;
      case NotificationType.QUOTE_INTEREST:
        this.onNewQuoteInterest.emit();
        break;
      case NotificationType.REQUEST_EXPIRED:
        this.onNewQuoteInterest.emit();
        this.onNewQuoteRequest.emit();
        this.onNewContactRequest.emit();
        break;
      case NotificationType.PARTNER_UPDATED:
        this.onPartnerUpdated.emit();
        break;
      case NotificationType.PARTNER_CREATED:
        this.onPartnerCreated.emit();
        break;
      case NotificationType.NEW_SUPPORT_REQUEST:
        this.onNewSupportRequest.emit();
        break;
      case NotificationType.BILLING:
        this.onWalletUpdate.emit();
        break;
      default:
        break;
    }
    this.onNewRequest.emit();
  }
}
